import { Card } from "@design";
import type { ReactNode } from "react";
import { withFieldGroup } from "@/components/form/form";

/**
 * The shell every Build entity form shares: a Card around a labelled form
 * whose native submit runs the TanStack form (so Enter in a field saves).
 * `readOnly` disables every field (a native disabled fieldset) and blocks submit;
 * leave out the save bar and say why with `notice`, shown above the fields.
 */
export function EntityFormCard({
  label,
  onSubmit,
  readOnly = false,
  notice,
  children,
}: Readonly<{ label: string; onSubmit: () => void; readOnly?: boolean; notice?: ReactNode; children: ReactNode }>) {
  return (
    <Card>
      <form
        aria-label={label}
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (!readOnly) onSubmit();
        }}
      >
        {notice}
        {readOnly ? (
          <fieldset disabled className="contents">
            {children}
          </fieldset>
        ) : (
          children
        )}
      </form>
    </Card>
  );
}

export interface IdentityFieldLabels {
  name: string;
  namePlaceholder: string;
  description: string;
  descriptionPlaceholder: string;
}

/** The `form.*` keys every Build entity namespace defines. */
type FormTextKey =
  | "form.name"
  | "form.namePlaceholder"
  | "form.description"
  | "form.descriptionPlaceholder"
  | "form.saved"
  | "form.saveFailed";
type FormCreateKey = "form.created" | "form.createFailed";

/**
 * Resolve the shared form labels and toast messages from an entity's own
 * translator (each namespace defines the same `form.*` keys), so forms don't
 * re-list them. `tc` is the same translator when the form also creates.
 */
export function formText(t: (key: FormTextKey) => string, tc?: (key: FormCreateKey) => string) {
  return {
    labels: {
      name: t("form.name"),
      namePlaceholder: t("form.namePlaceholder"),
      description: t("form.description"),
      descriptionPlaceholder: t("form.descriptionPlaceholder"),
    } satisfies IdentityFieldLabels,
    messages: {
      saved: t("form.saved"),
      saveFailed: t("form.saveFailed"),
      created: tc?.("form.created"),
      createFailed: tc?.("form.createFailed"),
    },
  };
}

/**
 * The Name + Description fields at the top of every Build entity form. Labels
 * come from the entity's own i18n namespace. Use with
 * `<IdentityFields form={form} fields={{ name: "name", description: "description" }} labels={...} />`.
 */
export const IdentityFields = withFieldGroup({
  defaultValues: { name: "", description: "" as string | null | undefined },
  props: {} as { labels: IdentityFieldLabels; descriptionRequired?: boolean; descriptionRows?: number },
  render: function Render({ group, labels, descriptionRequired = false, descriptionRows = 2 }) {
    return (
      <>
        <group.AppField name="name">
          {(field) => <field.TextField label={labels.name} required placeholder={labels.namePlaceholder} />}
        </group.AppField>
        <group.AppField name="description">
          {(field) => (
            <field.TextareaField
              label={labels.description}
              required={descriptionRequired}
              placeholder={labels.descriptionPlaceholder}
              rows={descriptionRows}
            />
          )}
        </group.AppField>
      </>
    );
  },
});
